import type { Locale } from '@/context/LangContext'

export interface SeoEntry {
  title: Record<Locale, string>
  description: Record<Locale, string>
}

export const seo: Record<string, SeoEntry> = {
  home: {
    title: {
      en: 'Lexoya | Cloud Servers, GPU, Managed Database & PaaS Hosting',
      fa: 'لکسویا | سرور ابری، GPU، دیتابیس مدیریت‌شده و پلتفرم ابری',
      ar: 'لکسویا | خوادم سحابية وGPU وقواعد بيانات مُدارة ومنصة PaaS',
    },
    description: {
      en: 'High-performance cloud VPS servers, GPU compute for AI, managed databases, S3 storage, and a full PaaS platform. Deploy globally in seconds with 99.99% uptime SLA.',
      fa: 'سرور مجازی ابری با عملکرد بالا، سرور GPU برای هوش مصنوعی، دیتابیس مدیریت‌شده، فضای ذخیره‌سازی S3 و پلتفرم ابری کامل. با SLA ۹۹.۹۹٪ در چند ثانیه دیپلوی کنید.',
      ar: 'خوادم VPS سحابية عالية الأداء، حوسبة GPU للذكاء الاصطناعي، قواعد بيانات مُدارة، تخزين S3، ومنصة PaaS كاملة. انشر عالميًا في ثوانٍ مع اتفاقية مستوى خدمة ٩٩.٩٩٪.',
    },
  },
  'vps-server': {
    title: {
      en: 'Cloud VPS Server Hosting — Lexoya | Instant Deploy, SSD NVMe',
      fa: 'خرید سرور مجازی (VPS) ابری — لکسویا | دیپلوی فوری با NVMe',
      ar: 'استضافة خادم VPS سحابي — لکسویا | نشر فوري مع SSD NVMe',
    },
    description: {
      en: 'High-performance virtual private servers with dedicated resources, full root access, and SSD NVMe storage. Deploy in seconds in Iran, Europe, or the US.',
      fa: 'سرور مجازی با منابع اختصاصی، دسترسی روت کامل و حافظه SSD NVMe. در چند ثانیه در ایران، اروپا یا آمریکا سرور خود را بسازید.',
      ar: 'خوادم افتراضية خاصة عالية الأداء بموارد مخصصة ووصول كامل للجذر وتخزين SSD NVMe. انشر في ثوانٍ في إيران أو أوروبا أو الولايات المتحدة.',
    },
  },
  gpu: {
    title: {
      en: 'GPU Servers for AI & Rendering — Lexoya | NVIDIA A100, H100',
      fa: 'سرور GPU برای هوش مصنوعی و رندر — لکسویا | NVIDIA A100، H100',
      ar: 'خوادم GPU للذكاء الاصطناعي والعرض — لکسویا | NVIDIA A100 وH100',
    },
    description: {
      en: 'Dedicated NVIDIA A100, H100, and L40S GPU servers for AI training, fine-tuning, rendering, and HPC workloads. Daily or monthly reservations, scale to multi-node clusters.',
      fa: 'سرور GPU اختصاصی NVIDIA A100، H100 و L40S برای آموزش مدل‌های هوش مصنوعی، فاین‌تیونینگ، رندر و HPC. رزرو روزانه یا ماهانه با قابلیت اسکیل تا کلاستر چندگره‌ای.',
      ar: 'خوادم GPU مخصصة NVIDIA A100 وH100 وL40S لتدريب نماذج الذكاء الاصطناعي والعرض وأعباء HPC. حجز يومي أو شهري مع توسع حتى مجموعات متعددة العقد.',
    },
  },
  'gpu-guide': {
    title: {
      en: 'What Is a GPU Server? Uses and How to Choose — Lexoya',
      fa: 'سرور GPU چیست؟ کاربردها و راهنمای انتخاب — لکسویا',
      ar: 'ما هو خادم GPU؟ الاستخدامات وطريقة الاختيار — لكسويا',
    },
    description: {
      en: 'What a GPU server is, how it differs from a CPU, when you actually need one, and how hourly, daily, monthly and yearly billing work.',
      fa: 'سرور GPU چیست، چه تفاوتی با CPU دارد و چه زمانی به آن نیاز دارید؟ کاربردها، انواع کارت اختصاصی و اشتراکی و شیوه‌های پرداخت ساعتی تا سالانه.',
      ar: 'ما هو خادم GPU، وكيف يختلف عن المعالج، ومتى تحتاجه فعلاً؟ الاستخدامات وأنواع البطاقات وطرق الدفع من الساعة إلى السنة.',
    },
  },
  'ai-server': {
    title: {
      en: 'AI Servers: Choosing a GPU for Training and Inference — Lexoya',
      fa: 'سرور هوش مصنوعی؛ انتخاب GPU برای آموزش و اجرای مدل — لکسویا',
      ar: 'خوادم الذكاء الاصطناعي: اختيار GPU للتدريب والتشغيل — لكسويا',
    },
    description: {
      en: 'How to choose a GPU for AI work: memory first, then bandwidth. RTX 4090, L40S, A100 80GB and H100 compared by workload, billed hourly to yearly.',
      fa: 'راهنمای انتخاب کارت گرافیک برای هوش مصنوعی: حافظه‌ی کارت، پهنای باند و مقایسه‌ی RTX 4090، L40S، A100 و H100 بر اساس نوع کار، با پرداخت ساعتی تا سالانه.',
      ar: 'كيف تختار بطاقة رسوميات لعمل الذكاء الاصطناعي: الذاكرة أولًا ثم عرض النطاق، ومقارنة RTX 4090 وL40S وA100 وH100 حسب نوع العمل.',
    },
  },
  paas: {
    title: {
      en: 'Cloud Platform (PaaS) | Deploy Your App from Git — Lexoya',
      fa: 'پلتفرم ابری (PaaS) لکسویا | دیپلوی اپلیکیشن از گیت',
      ar: 'منصة سحابية (PaaS) | انشر تطبيقك من Git — لكسويا',
    },
    description: {
      en: 'Deploy from your repository: automatic builds, a free HTTPS subdomain, managed domains and scaling. Billed hourly for the resources you allocate.',
      fa: 'اپلیکیشن را از ریپازیتوری دیپلوی کنید: بیلد خودکار، ساب‌دامین رایگان با SSL، دامنه و مقیاس‌دهی مدیریت‌شده. هزینه ساعتی و به‌ازای منابعی که تعیین می‌کنید.',
      ar: 'انشر تطبيقك من مستودعك: بناء تلقائي ونطاق فرعي مجاني بـSSL ونطاقات وتوسع مُدار. الفوترة ساعية حسب الموارد التي تحددها.',
    },
  },
  database: {
    title: {
      en: 'Managed Database (DBaaS) — Lexoya | PostgreSQL, MySQL, Redis',
      fa: 'دیتابیس مدیریت‌شده (DBaaS) — لکسویا | PostgreSQL، MySQL، Redis',
      ar: 'قاعدة بيانات مُدارة (DBaaS) — لکسویا | PostgreSQL وMySQL وRedis',
    },
    description: {
      en: 'High-availability managed database clusters with automated backups, replication, and failover. Launch PostgreSQL, MySQL, MariaDB, Redis, or MongoDB in seconds.',
      fa: 'خوشه‌های دیتابیس مدیریت‌شده با دسترسی بالا، پشتیبان‌گیری خودکار، رپلیکا و failover. در چند ثانیه PostgreSQL، MySQL، MariaDB، Redis یا MongoDB را راه‌اندازی کنید.',
      ar: 'مجموعات قواعد بيانات مُدارة عالية التوفر بنسخ احتياطية تلقائية وتكرار وتجاوز. أطلق PostgreSQL أو MySQL أو MariaDB أو Redis أو MongoDB في ثوانٍ.',
    },
  },
  storage: {
    title: {
      en: 'S3 Cloud Storage — Lexoya | Scalable Object Storage',
      fa: 'فضای ذخیره‌سازی ابری S3 — لکسویا | ذخیره‌سازی مقیاس‌پذیر',
      ar: 'تخزين سحابي S3 — لکسویا | تخزين كائنات قابل للتوسع',
    },
    description: {
      en: 'Durable, scalable object storage with a fully S3-compatible API, free cache layer, and cross-region backup. Store any amount of data with five nines of durability.',
      fa: 'فضای ذخیره‌سازی مقیاس‌پذیر با API کاملاً سازگار با S3، لایه کش رایگان و پشتیبان‌گیری بین‌منطقه‌ای. هر حجم از داده را با دوام پنج‌نه نگه‌داری کنید.',
      ar: 'تخزين كائنات متين وقابل للتوسع مع API متوافق تمامًا مع S3 وطبقة تخزين مؤقت مجانية ونسخ احتياطي بين المناطق. خزّن أي كمية من البيانات بديمومة ٩٩.٩٩٩٪.',
    },
  },
  backup: {
    title: {
      en: 'Automated Backup & Disaster Recovery — Lexoya',
      fa: 'پشتیبان‌گیری خودکار و بازیابی اطلاعات — لکسویا',
      ar: 'نسخ احتياطي تلقائي واستعادة الكوارث — لکسویا',
    },
    description: {
      en: 'Schedule automated backups for databases, cloud disks, and apps. Store snapshots across multiple regions and restore in seconds with one click.',
      fa: 'پشتیبان‌گیری خودکار از دیتابیس‌ها، دیسک‌های ابری و نرم‌افزارها. ذخیره بکاپ در چند منطقه جغرافیایی و بازیابی با یک کلیک.',
      ar: 'جدولة نسخ احتياطية تلقائية لقواعد البيانات والأقراص السحابية والتطبيقات. خزّن اللقطات في مناطق متعددة واستعدها بنقرة واحدة.',
    },
  },
  security: {
    title: {
      en: 'Security Services & Penetration Testing — Lexoya',
      fa: 'خدمات امنیتی و تست نفوذ — لکسویا',
      ar: 'الخدمات الأمنية واختبار الاختراق — لکسویا',
    },
    description: {
      en: 'Enterprise-grade penetration testing, vulnerability assessment, and security audits for your infrastructure. Protect your systems with expert security services.',
      fa: 'تست نفوذ در سطح سازمانی، ارزیابی آسیب‌پذیری و ممیزی امنیتی زیرساخت شما. با خدمات امنیتی متخصصان از سیستم‌های خود محافظت کنید.',
      ar: 'اختبار اختراق على مستوى المؤسسات وتقييم الثغرات وتدقيق أمني لبنيتك التحتية. احمِ أنظمتك بخدمات أمنية من الخبراء.',
    },
  },
  devops: {
    title: {
      en: 'DevOps Services — Lexoya | CI/CD, Kubernetes, Monitoring',
      fa: 'خدمات دواپس — لکسویا | CI/CD، کوبرنتیز، مانیتورینگ',
      ar: 'خدمات ديف أوبس — لکسویا | CI/CD وKubernetes والمراقبة',
    },
    description: {
      en: 'Managed CI/CD pipelines, dedicated Kubernetes clusters, Ceph, infrastructure as code, and team training from an experienced DevOps team.',
      fa: 'راه‌اندازی CI/CD، کلاستر اختصاصی کوبرنتیز، Ceph، زیرساخت به‌صورت کد و آموزش تیم فنی توسط تیم باتجربه دواپس لکسویا.',
      ar: 'خطوط CI/CD مُدارة ومجموعات Kubernetes مخصصة وCeph والبنية التحتية كرمز وتدريب للفرق من فريق ديف أوبس ذي الخبرة.',
    },
  },
  organizations: {
    title: {
      en: 'Organizational Services — Lexoya | Enterprise Cloud Governance',
      fa: 'خدمات سازمانی — لکسویا | مدیریت ابر سازمانی',
      ar: 'الخدمات المؤسسية — لکسویا | حوكمة سحابية للمؤسسات',
    },
    description: {
      en: 'Multi-account management, centralized authentication, team budgets, role-based access control, and audit logs for enterprises. Scale your organization with Lexoya.',
      fa: 'مدیریت متمرکز حساب‌ها، احراز هویت متمرکز، بودجه تیم‌ها، کنترل دسترسی نقش‌محور و لاگ ممیزی برای سازمان‌ها. با لکسویا سازمان خود را مقیاس‌پذیر کنید.',
      ar: 'إدارة متعددة الحسابات ومصادقة مركزية وميزانيات للفرق والتحكم بالوصول القائم على الأدوار وسجلات تدقيق للمؤسسات. وسّع مؤسستك مع لکسویا.',
    },
  },
  monitoring: {
    title: {
      en: 'Infrastructure Monitoring & Alerting — Lexoya',
      fa: 'مانیتورینگ زیرساخت و هشداردهی — لکسویا',
      ar: 'مراقبة البنية التحتية والتنبيه — لکسویا',
    },
    description: {
      en: 'Real-time observability across your entire infrastructure: metrics, logs, and traces with custom dashboards, tracing, and intelligent alerts for critical situations.',
      fa: 'مشاهده‌پذیری لحظه‌ای کل زیرساخت: متریک‌ها، لاگ‌ها و trace با داشبوردهای سفارشی و آلرت هوشمند برای شرایط بحرانی.',
      ar: 'مراقبة فورية لبنيتك التحتية بالكامل: مقاييس وسجلات وتتبعات مع لوحات مخصصة وتنبيهات ذكية للحالات الحرجة.',
    },
  },
  consulting: {
    title: {
      en: 'Cloud Consulting & Migration — Lexoya | Architecture Experts',
      fa: 'مشاوره و مهاجرت ابری — لکسویا | معماران زیرساخت خبره',
      ar: 'استشارات وهجرة سحابية — لکسویا | خبراء البنية التحتية',
    },
    description: {
      en: 'Architecture review, migration planning, and cloud cost optimization from certified architects. Design scalable systems and make the right infrastructure decisions.',
      fa: 'بررسی معماری، برنامه‌ریزی مهاجرت و بهینه‌سازی هزینه ابری توسط معماران خبره. سیستم‌های مقیاس‌پذیر طراحی کنید و تصمیم‌های درست زیرساختی بگیرید.',
      ar: 'مراجعة الهندسة وتخطيط الهجرة وتحسين تكاليف السحابة من مهندسين معتمدين. صمّم أنظمة قابلة للتوسع واتخذ قرارات البنية التحتية الصحيحة.',
    },
  },
  quickapps: {
    title: {
      en: 'Quick Apps — Lexoya | One-Click Deploy of 30+ Popular Apps',
      fa: 'برنامه‌های آماده — لکسویا | دیپلوی با یک کلیک بیش از ۳۰ اپ',
      ar: 'التطبيقات السريعة — لکسویا | نشر بنقرة واحدة لأكثر من ٣٠ تطبيقًا',
    },
    description: {
      en: 'Launch WordPress, GitLab, and 30+ popular open-source apps pre-configured in one click. No installation or maintenance hassle — we handle it all.',
      fa: 'وردپرس، گیت‌لب و بیش از ۳۰ برنامه متن‌باز محبوب را آماده و با یک کلیک اجرا کنید. بدون دردسر نصب و نگهداری — همه را ما انجام می‌دهیم.',
      ar: 'شغّل ووردبريس وGitLab وأكثر من ٣٠ تطبيقًا مفتوح المصدر جاهزًا بنقرة واحدة. دون عناء التثبيت أو الصيانة — نحن نتولى كل شيء.',
    },
  },
  streaming: {
    title: {
      en: 'Streaming Server — Lexoya | Live Stream & Media Delivery',
      fa: 'سرور استریم — لکسویا | لایو استریم و پخش رسانه',
      ar: 'خادم بث — لکسویا | بث مباشر وتوصيل الوسائط',
    },
    description: {
      en: 'High-performance live streaming and re-streaming infrastructure with dedicated bandwidth, global CDN, and optimized video delivery. Receive, optimize, and deliver streams.',
      fa: 'زیرساخت لایو استریم و ری‌استریم با عملکرد بالا، پهنای باند اختصاصی و CDN سراسری. استریم‌های ویدئویی را دریافت، بهینه و تحویل می‌دهیم.',
      ar: 'بنية تحتية للبث المباشر وإعادة البث عالية الأداء بنطاق ترددي مخصص وCDN عالمي. نستقبل البث ونحسّنه ونوصله.',
    },
  },
  sentry: {
    title: {
      en: 'Managed Sentry — Lexoya | Error Tracking & Monitoring',
      fa: 'سنتری مدیریت‌شده — لکسویا | ردیابی خطا و مانیتورینگ',
      ar: 'سنتري مُدار — لکسویا | تتبع الأخطاء والمراقبة',
    },
    description: {
      en: 'Identify, diagnose, and fix application errors in real time with Sentry. Shared or dedicated instances, fully managed by Lexoya.',
      fa: 'خطاهای برنامه را در زمان واقعی شناسایی، تشخیص و رفع کنید. سنتری اشتراکی یا اختصاصی، کاملاً مدیریت‌شده توسط لکسویا.',
      ar: 'حدد أخطاء التطبيقات وشخّصها وأصلحها في الوقت الفعلي مع سنتري. مثيلات مشتركة أو مخصصة مُدارة بالكامل من لکسویا.',
    },
  },
  mcp: {
    title: {
      en: 'Lexoya MCP Server | Manage Your Cloud from Claude, Cursor & AI Assistants',
      fa: 'سرور MCP لکسویا | مدیریت ابر با Claude، Cursor و دستیارهای هوش مصنوعی',
      ar: 'خادم MCP لکسویا | أدر سحابتك عبر Claude وCursor ومساعدي الذكاء الاصطناعي',
    },
    description: {
      en: 'Connect Claude, Cursor, or any MCP-capable assistant to your Lexoya account. Deploy projects, manage servers and databases, read build logs, and check your balance from your chat window.',
      fa: 'دستیار هوش مصنوعیتون رو به لکسویا وصل کنید: دیپلوی پروژه، مدیریت سرور و دیتابیس، خوندن لاگ بیلد و چک کردن موجودی از همون پنجره‌ی چت، با Model Context Protocol.',
      ar: 'اربط مساعد الذكاء الاصطناعي بحسابك في لکسویا: نشر المشاريع، وإدارة الخوادم وقواعد البيانات، وقراءة سجلات البناء، وفحص الرصيد من نافذة الدردشة عبر MCP.',
    },
  },
  pricing: {
    title: {
      en: 'Cloud Pricing — Lexoya | Transparent, No Surprise Fees',
      fa: 'قیمت‌گذاری ابری — لکسویا | شفاف و بدون هزینه پنهان',
      ar: 'تسعير السحابة — لکسویا | شفاف وبدون رسوم مفاجئة',
    },
    description: {
      en: 'Transparent, no-surprise pricing for cloud servers, GPU compute, S3 storage, PaaS, and one-click Quick Apps. Know exactly what you pay, every time.',
      fa: 'قیمت‌گذاری شفاف برای سرور ابری، GPU، فضای ذخیره‌سازی S3، پلتفرم ابری و برنامه‌های آماده. همیشه دقیقاً بدانید چه مبلغی می‌پردازید.',
      ar: 'تسعير شفاف بدون مفاجآت للخوادم السحابية وحوسبة GPU وتخزين S3 وPaaS والتطبيقات السريعة. اعرف بالضبط ما تدفعه في كل مرة.',
    },
  },
  'pricing/calculator': {
    title: {
      en: 'Cloud Cost Calculator — Lexoya | Estimate PaaS, Database & App Pricing',
      fa: 'ماشین حساب هزینه ابری — لکسویا | تخمین هزینه PaaS، دیتابیس و برنامه‌های آماده',
      ar: 'حاسبة التكلفة السحابية — لكسويا | تقدير أسعار PaaS وقواعد البيانات والتطبيقات',
    },
    description: {
      en: 'Estimate your monthly Lexoya bill in seconds. Add PaaS projects, managed databases, and quick apps, tune CPU, RAM, storage, and replicas, and see the live cost breakdown.',
      fa: 'هزینه ماهانه لکسویا را در چند ثانیه تخمین بزنید. پروژه‌های PaaS، دیتابیس‌های مدیریت‌شده و برنامه‌های آماده را اضافه کنید، CPU، RAM، حافظه و رپلیکا را تنظیم کنید و تفکیک هزینه را زنده ببینید.',
      ar: 'قدّر فاتورتك الشهرية في لكسويا خلال ثوانٍ. أضف مشاريع PaaS وقواعد البيانات المُدارة والتطبيقات السريعة، واضبط المعالج والذاكرة والتخزين والنسخ المكررة، وشاهد تفصيل التكلفة مباشرة.',
    },
  },
  docs: {
    title: {
      en: 'Documentation — Lexoya | Billing, Services & Guides',
      fa: 'مستندات — لکسویا | صورت‌حساب، سرویس‌ها و راهنماها',
      ar: 'الوثائق — لکسویا | الفواتير والخدمات والأدلة',
    },
    description: {
      en: 'Lexoya documentation: how billing works across all services — PaaS & QuickApps pay-as-you-go, VPS monthly plans, GPU reservations, S3 storage, network traffic overage, and the unified wallet billing orchestrator.',
      fa: 'مستندات لکسویا: نحوه محاسبه هزینه در همه سرویس‌ها — پلتفرم ابری و برنامه‌های آماده به‌صورت مصرفی، پلن ماهانه VPS، رزرو GPU، فضای S3، مازاد ترافیک و کیف پول یکپارچه.',
      ar: 'وثائق لکسویا: طريقة احتساب الفواتير في جميع الخدمات — PaaS والتطبيقات السريعة بالدفع حسب الاستخدام، والخطط الشهرية لـVPS، وحجوزات GPU، وتخزين S3، وتجاوز حركة المرور، ومحفظة الدفع الموحدة.',
    },
  },
  status: {
    title: {
      en: 'System Status — Lexoya | Uptime & Incidents',
      fa: 'وضعیت سرویس‌ها — لکسویا | آپ‌تایم و حوادث',
      ar: 'حالة الخدمات — لکسویا | وقت التشغيل والحوادث',
    },
    description: {
      en: 'Real-time status overview of Lexoya services, infrastructure, and network. Check uptime, incidents, and scheduled maintenance.',
      fa: 'نمای لحظه‌ای وضعیت سرویس‌ها، زیرساخت و شبکه لکسویا. آپ‌تایم، حوادث و نگهداری برنامه‌ریزی‌شده را بررسی کنید.',
      ar: 'نظرة فورية على حالة خدمات لکسویا وبنيتها التحتية وشبكتها. تحقق من وقت التشغيل والحوادث والصيانة المجدولة.',
    },
  },
  contact: {
    title: {
      en: 'Contact Us — Lexoya | Sales, Support & Engineering',
      fa: 'تماس با ما — لکسویا | فروش، پشتیبانی و مهندسی',
      ar: 'اتصل بنا — لکسویا | المبيعات والدعم والهندسة',
    },
    description: {
      en: 'Get in touch with Lexoya. Reach our sales, support, and engineering teams via phone, email, or live chat. We are here 24/7 to help.',
      fa: 'با لکسویا در تماس باشید. تیم فروش، پشتیبانی و مهندسی ما از طریق تلفن، ایمیل یا چت آنلاین در دسترس شماست. ۲۴/۷ کنار شما هستیم.',
      ar: 'تواصل مع لکسویا. فرق المبيعات والدعم والهندسة في خدمتك عبر الهاتف أو البريد الإلكتروني أو الدردشة المباشرة. نحن هنا على مدار الساعة.',
    },
  },
  about: {
    title: {
      en: 'About Us — Lexoya | Built by Engineers, for Engineers',
      fa: 'درباره لکسویا | ساخته‌شده توسط مهندسان، برای مهندسان',
      ar: 'معلومات عن لکسویا | بُني بواسطة مهندسين، للمهندسين',
    },
    description: {
      en: 'Learn about Lexoya — the cloud platform built for the next generation of applications. Our mission, values, and the team behind the infrastructure.',
      fa: 'با لکسویا آشنا شوید — پلتفرم ابری ساخته‌شده برای نسل بعدی اپلیکیشن‌ها. مأموریت، ارزش‌ها و تیمی که پشت این زیرساخت است.',
      ar: 'تعرّف على لکسویا — المنصة السحابية المصممة للجيل القادم من التطبيقات. رسالتنا وقيمنا والفريق الذي يقف خلف هذه البنية التحتية.',
    },
  },
  terms: {
    title: {
      en: 'Terms & Conditions — Lexoya',
      fa: 'قوانین و شرایط — لکسویا',
      ar: 'الشروط والأحكام — لکسویا',
    },
    description: {
      en: 'Lexoya Terms and Conditions govern the use of our cloud infrastructure services, including VPS, GPU, PaaS, storage, and related offerings.',
      fa: 'قوانین و شرایط لکسویا نحوه استفاده از خدمات زیرساخت ابری ما شامل VPS، GPU، پلتفرم ابری، ذخیره‌سازی و سایر خدمات را مشخص می‌کند.',
      ar: 'تنظّم شروط وأحكام لکسویا استخدام خدمات البنية التحتية السحابية لدينا، بما في ذلك VPS وGPU وPaaS والتخزين والعروض ذات الصلة.',
    },
  },
  privacy: {
    title: {
      en: 'Privacy Policy — Lexoya',
      fa: 'حریم خصوصی — لکسویا',
      ar: 'سياسة الخصوصية — لکسویا',
    },
    description: {
      en: 'Lexoya Privacy Policy explains how we collect, use, store, and protect your personal data when you use our cloud infrastructure services.',
      fa: 'سیاست حریم خصوصی لکسویا نحوه جمع‌آوری، استفاده، ذخیره‌سازی و محافظت از داده‌های شخصی شما هنگام استفاده از خدمات ابری را توضیح می‌دهد.',
      ar: 'توضح سياسة خصوصية لکسویا كيفية جمع بياناتك الشخصية واستخدامها وتخزينها وحمايتها عند استخدام خدماتنا السحابية.',
    },
  },
}